import { FC, useEffect, useMemo } from "react";
import RelatedTaxiRailyBillingTableRow from "./types/related-taxi-raily-billing-table-row";
import relatedTaxiRailyBillingsHelper from "./related-taxi-raily-billings.helper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import LinkButtonComponent from "../../../../../../common/components/button/link/link-button.component";
import CardComponent from "../../../../../../common/components/card/card.component";
import TableComponent from "../../../../../../common/components/table/table.component";
import dateService from "../../../../../../common/utils/date/date.service";
import billingsTranslationsHelper from "../../../../../../languages/billings-translations.helper";
import useBillingsOrderRelatedTaxiRaily from "../../../../../../common/services/billings/order/related/taxi-raily/use-billings-order-related-taxi-raily";
import useAbort from "../../../../../../common/hooks/use-abort";
import BillingsOrderRelatedTaxiRailyParams from "../../../../../../common/services/billings/order/related/taxi-raily/billings-order-related-taxi-raily-params";
import BillingsOrderRelatedTaxiRailyItem from "../../../../../../common/services/billings/order/related/taxi-raily/billings-order-related-taxi-raily";

type RelatedTaxiRailyBillingsProps = {
  orderId: string | undefined;
};

const RelatedTaxiRailyBillingsComponent: FC<RelatedTaxiRailyBillingsProps> = (
  props,
) => {
  const billingsOrderRelatedTaxiRaily = useBillingsOrderRelatedTaxiRaily();
  const billingsOrderRelatedTaxiRailyAbort = useAbort();

  useEffect(() => {
    if (props.orderId) {
      const params: BillingsOrderRelatedTaxiRailyParams = {
        orderUuid: props.orderId,
      };

      billingsOrderRelatedTaxiRaily.load(
        params,
        billingsOrderRelatedTaxiRailyAbort.signal,
      );

      return billingsOrderRelatedTaxiRailyAbort.revoke;
    }
  }, [props.orderId]);

  const createTableRow = (
    billingData: BillingsOrderRelatedTaxiRailyItem,
  ): RelatedTaxiRailyBillingTableRow => {
    return {
      id: billingData.cargoOrder.humanId.toString(),
      value: {
        date: (
          <div title={dateService.format(billingData.date, "dd/mm/yyyy")}>
            {dateService.format(billingData.date, "dd/mm/yyyy")}
          </div>
        ),
        internalOrderId: (
          <div title={billingData.cargoOrder.humanId.toString()}>
            {billingData.cargoOrder.humanId}
          </div>
        ),
        companyName: (
          <div title={billingData.contract.cargoCompany.companyName}>
            {billingData.contract.cargoCompany.companyName}
          </div>
        ),
        model: (
          <div title={billingData.contract.model}>
            {billingData.contract.model}
          </div>
        ),
        rate: (
          <div title={billingData.contract.distanceRate.toString()}>
            {billingData.contract.distanceRate.toFixed(2)} PLN
          </div>
        ),
        distance: (
          <div title={billingData.distance.toString()}>
            {billingData.distance}
          </div>
        ),
        routeDistanceCost: (
          <div title={billingData.discountAmount.toString()}>
            {billingData.discountAmount.toFixed(2)} PLN
          </div>
        ),
        discount: (
          <div title={billingData?.discount.toString()}>
            {billingData.discount.toFixed(2)}%
          </div>
        ),
        discountTotal: (
          <div title={billingData.discountAmount.toString()}>
            {billingData.discountAmount.toFixed(2)} PLN
          </div>
        ),
        haltingCost: (
          <div title={billingData.haltingAmount.toString()}>
            {billingData.haltingAmount.toFixed(2)} PLN
          </div>
        ),
        highwayCost: (
          <div title={billingData.highwayCharge.toString()}>
            {billingData.highwayCharge.toFixed(2)} PLN
          </div>
        ),
        total: (
          <div title={billingData.totalAmount.toString()}>
            {billingData.totalAmount.toFixed(2)} PLN
          </div>
        ),
        actions: (
          <LinkButtonComponent
            type="primary"
            to={""}
            classNames={{
              root: "related_order_billings_table_edit_billing_icon",
            }}
          >
            <FontAwesomeIcon icon={faEdit} />
          </LinkButtonComponent>
        ),
      },
    };
  };

  const tableColumns = relatedTaxiRailyBillingsHelper.getTableColumns();

  const tableRows: RelatedTaxiRailyBillingTableRow[] = useMemo(() => {
    if (billingsOrderRelatedTaxiRaily.data) {
      return billingsOrderRelatedTaxiRaily.data.map((listingItem) =>
        createTableRow(listingItem),
      );
    }

    return [];
  }, [billingsOrderRelatedTaxiRaily.data]);

  const translations =
    billingsTranslationsHelper.getRailyTaxiAddBillingsTranslations()
      .relatedBillings.cargo_raily;

  return (
    <CardComponent
      classNames={{ root: "related_order_billings" }}
      header={{ title: translations.summaryLabel }}
    >
      <TableComponent
        isLoading={billingsOrderRelatedTaxiRaily.isLoading}
        columns={tableColumns}
        rows={tableRows}
      />
    </CardComponent>
  );
};

export default RelatedTaxiRailyBillingsComponent;
